import * as React from 'react';
import PropTypes from 'prop-types';
import {
  TimesheetEntryItemCard,
  TimesheetEntryTitle,
  TimesheetEntryItemCardInfo,
  TimesheetEntrySubText,
  TimesheetEntryTimesWrapper
} from './TimesheetEntryItem.style';
import TimesheetEntryTimeField from '../TimesheetEntryTimeField/TimesheetEntryTimeField';
import YPTimesheetEntryObject from '../../yesplan_classes/YPTimesheetEntryObject';
import FeedbackMessage from '../../components/FeedbackMessage/FeedbackMessage';
import { Body } from '../../styled components/Typography';

class TimesheetEntryItem extends React.Component {
  constructor(props) {
    super(props);
    this.timesheetEntryObject = props.timesheetEntryObject;
    this.startTimeInputRef = React.createRef();
    this.endTimeInputRef = React.createRef();
  }

  canPerformTimeChangeOnStartTime(proposedDateAndTime) {
    return (
      this.timesheetEntryObject.canPerformTimeChangeOnTimesheetStartTime(
        proposedDateAndTime
      ) ||
      (this.timesheetEntryObject.hasInvalidTimesWarning() &&
        this.timesheetEntryObject.canPerformTimeChangeWithoutExceedingDeltaForStart(
          proposedDateAndTime
        ))
    );
  }

  canPerformTimeChangeOnEndTime(proposedDateAndTime) {
    return (
      this.timesheetEntryObject.canPerformTimeChangeOnTimesheetEndTime(
        proposedDateAndTime
      ) ||
      (this.timesheetEntryObject.hasInvalidTimesWarning() &&
        this.timesheetEntryObject.canPerformTimeChangeWithoutExceedingDeltaForEnd(
          proposedDateAndTime
        ))
    );
  }

  renderTitle() {
    const role = this.timesheetEntryObject.getRoleTitle();
    return (
      <TimesheetEntryTitle $variant={this.timesheetEntryObject.getType()}>
        {this.timesheetEntryObject.getTitle()}
        {role && `, ${role}`}
      </TimesheetEntryTitle>
    );
  }

  renderSubText() {
    if (
      !this.timesheetEntryObject.hasLinkedEventWithLocation() &&
      !this.timesheetEntryObject.linkedBookingComment()
    )
      return null;
    return (
      <div>
        {this.timesheetEntryObject.hasLinkedEventWithLocation() && (
          <TimesheetEntrySubText>
            {this.timesheetEntryObject.getBookingName()}
          </TimesheetEntrySubText>
        )}
        {this.timesheetEntryObject.linkedBookingComment() && (
          <TimesheetEntrySubText tw="mt-8">
            {this.timesheetEntryObject.linkedBookingComment()}
          </TimesheetEntrySubText>
        )}
      </div>
    );
  }

  renderEmptySubmittedTime() {
    return (
      <div tw="h-24 text-right" data-test-empty-time>
        <Body $color="black">--:--</Body>
      </div>
    );
  }

  renderStartTime() {
    const {
      handleTimeEdit,
      timesheetEntryObject,
      canEdit,
      autosaveTimesheet,
      processTimeChange,
      timesheetInFinalState
    } = this.props;
    const timesheetEntryID = this.timesheetEntryObject.getIdWithOnlyStartOrEnd();
    const dateTime = !canEdit
      ? timesheetEntryObject.getTimesheetStart(false)
      : timesheetEntryObject.getTimesheetStartEdit();

    if (dateTime === null) return this.renderEmptySubmittedTime();
    return (
      <TimesheetEntryTimeField
        inputRef={this.startTimeInputRef}
        dateTime={dateTime}
        delta={timesheetEntryObject.getDeltaDurationForStart()}
        onChangeFn={(value) => {
          handleTimeEdit(timesheetEntryID, {
            filledInStartTime: value
          });
        }}
        processTimeChangeFn={() => {
          processTimeChange(timesheetEntryID);
        }}
        canPerformTimeChange={this.canPerformTimeChangeOnStartTime}
        isValidInput={timesheetEntryObject.isValidForStartTime()}
        id={this.timesheetEntryObject.getIdWithOnlyStartOrEnd()}
        timesheetEntryObject={timesheetEntryObject}
        canEdit={canEdit}
        isCancelled={
          timesheetEntryObject.hasActualStatusCancelled() && timesheetInFinalState
        }
        autosaveTimesheet={autosaveTimesheet}
      />
    );
  }

  renderEndTime() {
    const {
      handleTimeEdit,
      timesheetEntryObject,
      canEdit,
      autosaveTimesheet,
      processTimeChange,
      timesheetInFinalState
    } = this.props;
    const timesheetEntryID = this.timesheetEntryObject.getIdWithOnlyStartOrEnd();
    const dateTime = !canEdit
      ? timesheetEntryObject.getTimesheetEnd(false)
      : timesheetEntryObject.getTimesheetEndEdit();

    if (dateTime === null) return this.renderEmptySubmittedTime();
    return (
      <TimesheetEntryTimeField
        inputRef={this.endTimeInputRef}
        dateTime={dateTime}
        delta={timesheetEntryObject.getDeltaDurationForEnd()}
        onChangeFn={(value) => {
          handleTimeEdit(timesheetEntryID, {
            filledInEndTime: value
          });
        }}
        processTimeChangeFn={() => {
          processTimeChange(timesheetEntryID);
        }}
        canPerformTimeChange={this.canPerformTimeChangeOnEndTime}
        isValidInput={timesheetEntryObject.isValidForEndTime()}
        id={this.timesheetEntryObject.getIdWithOnlyStartOrEnd()}
        timesheetEntryObject={timesheetEntryObject}
        canEdit={canEdit}
        isCancelled={
          timesheetEntryObject.hasActualStatusCancelled() && timesheetInFinalState
        }
        autosaveTimesheet={autosaveTimesheet}
      />
    );
  }

  renderWarnings() {
    const { timesheetEntryObject, canEdit } = this.props;
    if (!timesheetEntryObject.hasWarnings()) return null;
    return (
      <div tw="space-y-4">
        {timesheetEntryObject.getWarnings().map((warning, i) => {
          const warningVariant = warning.getVariant();
          if (!canEdit && !warning.showWarningWhenTimesheetIsNotEditable()) return null;
          return (
            <FeedbackMessage
              variant={warningVariant}
              key={`${i}-warning`}
              feedbackTexts={warning.getWarningText()}
              dataAttributes={{
                'data-test-timesheet-entry-feedback-message': warningVariant
              }}
            />
          );
        })}
      </div>
    );
  }

  render() {
    const { timesheetEntryObject, canEdit } = this.props;
    return (
      <TimesheetEntryItemCard
        $variant={timesheetEntryObject.getType()}
        $shiftPart={timesheetEntryObject.getShowOnlyStartOrEnd()}
        data-test-timesheetentry
      >
        {timesheetEntryObject.getShowOnlyStartOrEnd() === 'end' && this.renderWarnings()}
        <TimesheetEntryItemCardInfo
          $isReadOnly={!canEdit}
          $fillSpaceReservedForFeedbackMessage={!timesheetEntryObject.hasWarnings()}
          $variant={timesheetEntryObject.getType()}
          $shiftPart={timesheetEntryObject.getShowOnlyStartOrEnd()}
        >
          <div>
            {this.renderTitle()}
            {this.renderSubText()}
          </div>
          <TimesheetEntryTimesWrapper
            $isReadOnly={!canEdit}
            $variant={timesheetEntryObject.getType()}
          >
            {timesheetEntryObject.shouldShowStart() && this.renderStartTime()}
            {timesheetEntryObject.shouldShowEnd() && this.renderEndTime()}
          </TimesheetEntryTimesWrapper>
        </TimesheetEntryItemCardInfo>

        {timesheetEntryObject.getShowOnlyStartOrEnd() !== 'end' && this.renderWarnings()}
      </TimesheetEntryItemCard>
    );
  }
}

TimesheetEntryItem.propTypes = {
  timesheetEntryObject: PropTypes.instanceOf(YPTimesheetEntryObject).isRequired,
  handleTimeEdit: PropTypes.func.isRequired,
  processTimeChange: PropTypes.func.isRequired,
  canEdit: PropTypes.bool.isRequired,
  timesheetInFinalState: PropTypes.bool,
  autosaveTimesheet: PropTypes.func.isRequired
};

TimesheetEntryItem.defaultProps = {
  timesheetInFinalState: false
};

export default TimesheetEntryItem;
